import type { DocumentRecord } from '@/types/catalog'

export interface OptimisticDocArgs {
  /** Real server document id from the upload response — keeps the row dedupe-able. */
  id: string
  name: string
  /** UI/API type (cast to DocumentRecord['type']). */
  type: string
  /** ISO timestamp used for both created_at and upload_started_at (DESC sort). */
  uploadStartedAt: string
  fileSize?: number | null
  userId?: string
  uploaderName?: string | null
  tenantId?: string
  /** Extra properties (e.g. multi-page grouping) merged into the row. */
  properties?: Partial<DocumentRecord['properties']>
  /**
   * Row status. Defaults to 'processing' — the state a file is in once its
   * bytes have landed. Pass 'uploading' for a row created at the START of an
   * upload (FS-499 pre-registration), which is what the server writes for a
   * pending row and what `CatalogTableV2` reads to render the row as not-yet
   * selectable.
   */
  status?: DocumentRecord['status']
}

/**
 * Build the optimistic row for an upload (FS-555).
 *
 * Mirrors V1's createTempDocument. The default status is 'processing' — the
 * state a file is in once its bytes are up, matching what a manual refresh
 * would show. Callers that pre-register the batch hold a server id BEFORE the
 * bytes go up and pass status 'uploading' instead, which is exactly what
 * `/v2/files/register-pending` writes on the row this id names, so the
 * optimistic row and the server row agree from the first frame.
 */
export function buildOptimisticDocument(args: OptimisticDocArgs): DocumentRecord {
  return {
    id: args.id,
    name: args.name,
    description: '',
    created_at: args.uploadStartedAt,
    upload_started_at: args.uploadStartedAt,
    user_id: args.userId ?? '',
    uploader_name: args.uploaderName ?? null,
    tenant_id: args.tenantId ?? '',
    type: args.type as DocumentRecord['type'],
    is_indexed: false,
    location: null,
    status: args.status ?? 'processing',
    file_size: args.fileSize ?? null,
    properties: {
      pnid: undefined,
      row_count: undefined,
      graph_id: undefined,
      ...args.properties,
    },
  }
}
